import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext'; // Import the hook
import data from '../../data/data.json';
import ColorPalette from './ColorPalette';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import pencilCursor from './pencilCursor';

// A colouring page only ever gets looked at on a screen or printed on A4, and
// Safari on iPhone and iPad refuses canvases much over 16 million pixels. The
// drawings are traced at roughly 7500 units across, so without a cap the
// download silently produced a blank image on exactly the devices children
// are most likely to use.
const MAX_DOWNLOAD_EDGE = 2000;

// Work is kept per page, so turning to page 7 and back finds page 1 exactly as
// it was left. Shapes are addressed by their position in the drawing, which is
// stable for a given file.
const STORAGE_KEY = 'mfc.coloring.v1';

function loadSaved() {
    try {
        return JSON.parse(window.localStorage.getItem(STORAGE_KEY)) || {};
    } catch (e) {
        return {};
    }
}

function persist(all) {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
    } catch (e) {
        // Out of quota or private browsing: colouring still works, it just
        // will not come back next time.
    }
}

function ColorBook() {
    const { lang, translate } = useLanguage();
    const [currentPage, setCurrentPage] = useState(0);
    useDocumentTitle(translate('colorBook'));
    const [selectedColor, setSelectedColor] = useState('#000000');
    const [svgDoc, setSvgDoc] = useState(null);
    const [history, setHistory] = useState([]);

    // The click handler is attached once per drawing, so it reads the colour
    // through a ref rather than closing over it. Re-attaching on every colour
    // change used to add a listener per shape and never remove it.
    const selectedColorRef = useRef(selectedColor);
    useEffect(() => {
        selectedColorRef.current = selectedColor;
    }, [selectedColor]);

    const pageKeyRef = useRef(null);

    const saveCurrent = useCallback((doc, pageKey) => {
        if (!doc || !pageKey) return;
        const shapes = doc.querySelectorAll('path, rect, circle');
        const changed = {};
        shapes.forEach((el, i) => {
            const fill = el.getAttribute('fill');
            if (fill && fill !== el.dataset.originalFill) changed[i] = fill;
        });
        const all = loadSaved();
        if (Object.keys(changed).length) all[pageKey] = changed;
        else delete all[pageKey];
        persist(all);
    }, []);

    const page = data.colorBook.pages[currentPage];
    const totalPages = data.colorBook.pages.length;
    const pageLanguageData = page?.lang?.[lang] || page?.lang?.en;

    const handleColorChange = (color) => setSelectedColor(color);

    const goToPage = (next) => setCurrentPage(next);

    const handleNextPage = () => goToPage((currentPage + 1) % totalPages);
    const handlePreviousPage = () => goToPage((currentPage - 1 + totalPages) % totalPages);

    const handleSvgClick = useCallback((event) => {
        const target = event.target;
        if (!['path', 'rect', 'circle'].includes(target.tagName.toLowerCase())) return;

        const previous = target.getAttribute('fill');
        if (previous === selectedColorRef.current) return;

        setHistory((past) => [...past, { el: target, fill: previous }]);
        target.setAttribute('fill', selectedColorRef.current);
        saveCurrent(target.ownerDocument, pageKeyRef.current);
    }, [saveCurrent]);

    const handleUndo = () => {
        if (history.length === 0) return;
        const last = history[history.length - 1];
        if (last.fill === null) {
            last.el.removeAttribute('fill');
        } else {
            last.el.setAttribute('fill', last.fill);
        }
        setHistory(history.slice(0, -1));
        saveCurrent(svgDoc, pageKeyRef.current);
    };

    const resetSvgColors = () => {
        if (!svgDoc) return;
        svgDoc.querySelectorAll('path, rect, circle').forEach((el) => {
            el.setAttribute('fill', el.dataset.originalFill);
        });
        setHistory([]);
        saveCurrent(svgDoc, pageKeyRef.current);
    };

    const downloadImage = () => {
        if (!svgDoc) return;

        const svgElement = svgDoc.documentElement;
        const svgData = new XMLSerializer().serializeToString(svgElement);
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');

        const image = new Image();
        const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(svgBlob);

        const svgUrl = page.svg;
        const fileName = svgUrl.substring(svgUrl.lastIndexOf('/') + 1).replace('.svg', '.png');

        image.onload = () => {
            const viewBox = svgElement.getAttribute('viewBox');
            const [, , width, height] = viewBox
                ? viewBox.split(' ').map(Number)
                : [0, 0, 1200, 1200];

            const scaleFactor = Math.min(2, MAX_DOWNLOAD_EDGE / Math.max(width, height));

            canvas.width = Math.round(width * scaleFactor);
            canvas.height = Math.round(height * scaleFactor);
            context.scale(scaleFactor, scaleFactor);
            context.drawImage(image, 0, 0, width, height);

            URL.revokeObjectURL(url);

            const a = document.createElement('a');
            a.download = fileName;
            a.href = canvas.toDataURL('image/png');
            a.click();
        };

        image.src = url;
    };

    useEffect(() => {
        const svgObject = document.getElementById('colorableSvg');
        if (!svgObject) return;

        const onLoad = () => setSvgDoc(svgObject.contentDocument);
        svgObject.addEventListener('load', onLoad);

        // The browser may have finished loading before this ran, in which case
        // the load event never fires again and the drawing would be inert.
        if (svgObject.contentDocument?.documentElement) {
            setSvgDoc(svgObject.contentDocument);
        }

        return () => svgObject.removeEventListener('load', onLoad);
    }, [currentPage]);

    useEffect(() => {
        if (!svgDoc) return;

        const shapes = svgDoc.querySelectorAll('path, rect, circle');
        shapes.forEach((el) => {
            if (!el.dataset.originalFill) {
                el.dataset.originalFill = el.getAttribute('fill') || 'none';
            }
            el.addEventListener('click', handleSvgClick);
        });

        // Put back whatever was left here last time.
        const pageKey = page?.svg;
        pageKeyRef.current = pageKey;
        const saved = loadSaved()[pageKey];
        if (saved) {
            Object.entries(saved).forEach(([i, fill]) => {
                const el = shapes[Number(i)];
                if (el) el.setAttribute('fill', fill);
            });
        }
        setHistory([]);

        return () => shapes.forEach((el) => el.removeEventListener('click', handleSvgClick));
    }, [svgDoc, handleSvgClick, page]);

    // The pencil follows the chosen colour. Paths that take no clicks — the
    // outlines — keep the normal arrow so the page reads correctly.
    useEffect(() => {
        if (!svgDoc) return;
        const cursor = pencilCursor(selectedColor);
        svgDoc.querySelectorAll('path, rect, circle').forEach((el) => {
            el.style.cursor = el.hasAttribute('pointer-events') ? 'default' : cursor;
        });
    }, [svgDoc, selectedColor]);

    return (
        <div className="color-book">
            <h1 className="visually-hidden">{translate('colorBook')}</h1>
            <div className="coloring-layout">
                {/* Text Section */}
                <div className="text-section">
                    <div className="text-image-container">
                        <img
                            src={page?.pageIllustration}
                            alt={translate('illustration_alt')}
                            className="text-image"
                        />
                    </div>
                    <div className="text-and-audio-container">
                        <div className="text-and-audio">
                            {pageLanguageData?.text ? (
                                pageLanguageData.text.split('\n').map((line, index) => (
                                    <p key={index} className={index === 0 ? 'first-paragraph' : ''}>{line}</p>
                                ))
                            ) : (
                                <p>{translate('no_text_for_language')}</p>
                            )}
                        </div>
                        <div className="page-number">
                            {`${currentPage + 1} ${translate('of')} ${totalPages}`}
                        </div>
                    </div>
                </div>

                {/* SVG Section */}
                <div className="svg-section">
                    <object
                        id="colorableSvg"
                        type="image/svg+xml"
                        data={page?.svg}
                        className="coloring-svg"
                        aria-label={translate('coloring_page_alt')}
                    ></object>
                </div>

                {/* Palette Section */}
                <div className="palette-section">
                    <ColorPalette selectedColor={selectedColor} onChange={handleColorChange} />
                </div>
            </div>

            {/* Navigation Buttons */}
            <div className="page-navigation">
                <button className="button" onClick={downloadImage} aria-label={translate('download')}>
                    <img src="/assets/img/icons/download-white.png" alt="" />
                </button>
                <button className="button" onClick={handlePreviousPage} aria-label={translate('previous')}>
                    <img src="/assets/img/icons/arrow-left.png" alt="" />
                </button>
                <button className="button" onClick={handleNextPage} aria-label={translate('next')}>
                    <img src="/assets/img/icons/arrow-right.png" alt="" />
                </button>
                <button
                    className="button"
                    onClick={handleUndo}
                    disabled={history.length === 0}
                    aria-label={translate('undo')}
                    title={translate('undo')}
                >
                    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" className="button-glyph">
                        <path
                            d="M8 7h7a5 5 0 0 1 0 10h-3"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        <path
                            d="M11 4 7.2 7l3.8 3"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </button>
                <button className="button" onClick={resetSvgColors} aria-label={translate('reset')}>
                    <img src="/assets/img/icons/clear-image.png" alt="" />
                </button>
            </div>
        </div>
    );
}

export default ColorBook;
